/** One-click, still-editable field suggestions (#378). `title` is the compact native tooltip for the evidence. */
import { Sparkles, X } from 'lucide-react'

export interface SuggestionChipValue {
  id: string
  why: string
  source: 'rule' | 'riley'
  confidence: number
}

export function SuggestionChips<T extends SuggestionChipValue>({
  suggestions,
  label,
  selectedId,
  onPick,
  onIgnore,
  prominent = false,
  hotkeys = false,
}: {
  suggestions: T[]
  label: (suggestion: T) => string
  selectedId?: string
  onPick: (suggestion: T) => void
  onIgnore: (suggestion: T) => void
  prominent?: boolean
  hotkeys?: boolean
}) {
  if (!suggestions.length) return null
  return (
    <fieldset
      aria-label={prominent ? 'Next move suggestions' : 'Suggestions'}
      className={prominent ? 'rounded-md border border-riley-border bg-riley-soft/50 p-2' : undefined}
    >
      {prominent ? (
        <p className="mb-1.5 flex items-center gap-1 text-xs font-medium text-riley">
          <Sparkles className="size-3" /> Suggested next moves
        </p>
      ) : null}
      {prominent ? null : <p className="mb-1.5 text-xs font-medium text-muted-foreground">Suggested</p>}
      <div className="flex flex-wrap gap-2">
        {suggestions.slice(0, 3).map((suggestion, index) => (
          <span key={suggestion.id} className="inline-flex max-w-full items-stretch">
            <button
              type="button"
              title={suggestion.why}
              aria-label={`${label(suggestion)}. Why: ${suggestion.why}`}
              aria-pressed={selectedId === suggestion.id}
              className={`min-h-9 max-w-full cursor-pointer rounded-l-md border px-2.5 py-1 text-left text-xs transition-colors ${
                selectedId === suggestion.id
                  ? 'border-riley bg-riley text-white'
                  : 'border-riley-border bg-card text-foreground hover:bg-riley-soft'
              }`}
              onClick={() => onPick(suggestion)}
            >
              {label(suggestion)}
              {hotkeys ? <span className="ml-1 text-[10px] opacity-70">Alt+{index + 1}</span> : null}
            </button>
            <button
              type="button"
              title={`Ignore: ${suggestion.why}`}
              aria-label={`Ignore suggestion: ${label(suggestion)}`}
              className="cursor-pointer rounded-r-md border border-l-0 border-riley-border bg-card px-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
              onClick={() => onIgnore(suggestion)}
            >
              <X className="size-3" />
            </button>
          </span>
        ))}
      </div>
    </fieldset>
  )
}
